import { DEFAULT_CHAT_TIMEZONE, normalizeChatTimezone } from './chatTimezone'

export const ENVIRONMENT_SCHEDULE_DAYS = [
  { value: 0, short: 'Sun', label: 'Sunday' },
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
] as const

export interface EnvironmentScheduleFields {
  days: number[]
  time: string
  timezone: string
}

function expandDayField(field: string): number[] | null {
  if (field === '*') return ENVIRONMENT_SCHEDULE_DAYS.map((day) => day.value)
  const days = new Set<number>()
  for (const part of field.split(',')) {
    const range = /^(\d)-(\d)$/.exec(part)
    if (range) {
      const start = Number(range[1])
      const end = Number(range[2])
      if (start > end || end > 6) return null
      for (let day = start; day <= end; day++) days.add(day)
    } else {
      const day = Number(part)
      if (!Number.isInteger(day) || day < 0 || day > 6) return null
      days.add(day)
    }
  }
  return [...days].sort((a, b) => a - b)
}

export function environmentScheduleFields(config: Record<string, unknown>): EnvironmentScheduleFields {
  let days: number[] = ENVIRONMENT_SCHEDULE_DAYS.map((day) => day.value)
  let time = '21:00'
  if (config.cadence === 'weekly') days = [0]
  else if (config.cadence === 'nightly-weekdays') days = [1, 2, 3, 4, 5]
  else if (typeof config.cron === 'string') {
    const fields = config.cron.trim().split(/\s+/)
    const minute = Number(fields[0])
    const hour = Number(fields[1])
    const parsedDays = fields.length === 5 ? expandDayField(fields[4]!) : null
    if (Number.isInteger(minute) && minute >= 0 && minute <= 59 && Number.isInteger(hour) && hour >= 0 && hour <= 23 && parsedDays?.length) {
      time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
      days = parsedDays
    }
  }
  return {
    days,
    time,
    timezone: normalizeChatTimezone(config.timezone),
  }
}

export function environmentScheduleCron(days: number[], time: string): string | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time)
  if (!match || days.length === 0) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  const validDays = [...new Set(days)].sort((a, b) => a - b)
  if (hour > 23 || minute > 59 || validDays.some((day) => !Number.isInteger(day) || day < 0 || day > 6)) return null
  const dayField = validDays.length === 7 ? '*' : validDays.join(',')
  return `${minute} ${hour} * * ${dayField}`
}

export function describeEnvironmentSchedule(config: Record<string, unknown>): string {
  const fields = environmentScheduleFields(config)
  const allDays = fields.days.length === 7
  const weekdays = fields.days.join(',') === '1,2,3,4,5'
  const days = allDays
    ? 'Every day'
    : weekdays
      ? 'Weekdays'
      : fields.days.map((value) => ENVIRONMENT_SCHEDULE_DAYS.find((day) => day.value === value)?.short).filter(Boolean).join(', ')
  const [hourText, minuteText] = fields.time.split(':')
  const hour = Number(hourText)
  const displayHour = hour % 12 || 12
  return `${days} at ${displayHour}:${minuteText} ${hour < 12 ? 'AM' : 'PM'} · ${fields.timezone || DEFAULT_CHAT_TIMEZONE}`
}
